/**
 * Form Service
 * Handles all CRUD operations for employer forms, form-job attachments, and form responses
 * 
 * SECURITY NOTES:
 * - All queries use parameterized inputs (ORM) - NO SQL INJECTION POSSIBLE
 * - All operations verify employer ownership (employerId parameter)
 * - RLS policies enforce data isolation at database level
 * - Input validation prevents buffer overflow and DoS attacks
 * - No sensitive data logged; errors are user-friendly
 * - See docs/04_Features/EMPLOYER_FORMS_SECURITY.md for full security audit
 */

import { supabase } from '../utils/supabase';
import { SupabaseError } from '../utils/errorHandling';
import cacheService from './cacheService';

const formService = {
  /**
   * Get all forms for an employer
   * @param {string} employerId - The employer's user ID
   * @returns {Promise<{success: boolean, data?: array, error?: string}>}
   */
  getEmployerForms: async (employerId) => {
    try {
      if (!employerId) {
        return { success: false, error: 'Employer ID is required' };
      }

      const { data, error } = await supabase
        .from('employer_forms')
        .select(`
          *,
          job_forms(
            id,
            job_id,
            jobs(id, title, status)
          )
        `)
        .eq('employer_id', employerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching employer forms:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'employer_forms', action: 'fetch' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Transform to include job count and job list
      const formsWithJobInfo = (data || []).map(form => ({
        ...form,
        jobCount: form.job_forms?.length || 0,
        jobs: form.job_forms?.map(jf => jf.jobs).filter(Boolean) || [],
        questionCount: Array.isArray(form.questions) ? form.questions.length : 0
      }));

      return { success: true, data: formsWithJobInfo };
    } catch (error) {
      console.error('Error in getEmployerForms:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'getEmployerForms' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Get a single form by ID
   * @param {string} formId - The form ID
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  getFormById: async (formId, employerId) => {
    try {
      if (!formId) {
        return { success: false, error: 'Form ID is required' };
      }

      const { data, error } = await supabase
        .from('employer_forms')
        .select(`
          *,
          job_forms(
            id,
            job_id,
            jobs(id, title, status)
          )
        `)
        .eq('id', formId)
        .eq('employer_id', employerId)
        .single();

      if (error) {
        console.error('Error fetching form:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'employer_forms', action: 'fetch_by_id' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Add computed fields
      const formWithJobInfo = {
        ...data,
        jobCount: data.job_forms?.length || 0,
        jobs: data.job_forms?.map(jf => jf.jobs).filter(Boolean) || [],
        questionCount: Array.isArray(data.questions) ? data.questions.length : 0
      };

      return { success: true, data: formWithJobInfo };
    } catch (error) {
      console.error('Error in getFormById:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'getFormById' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Create a new form
   * @param {object} formData - Form data { title, description, questions, employer_id }
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  createForm: async (formData) => {
    try {
      const { title, description, questions, employer_id, status = 'draft' } = formData;

      if (!title || !employer_id) {
        return { success: false, error: 'Title and employer ID are required' };
      }

      // Validate title length
      if (title.length < 1 || title.length > 200) {
        return { success: false, error: 'Title must be between 1 and 200 characters' };
      }

      // Validate description length if provided
      if (description && description.length > 1000) {
        return { success: false, error: 'Description must not exceed 1000 characters' };
      }

      // Ensure questions is valid JSON array
      const validQuestions = Array.isArray(questions) ? questions : [];

      const { data, error } = await supabase
        .from('employer_forms')
        .insert({
          title,
          description: description || null,
          questions: validQuestions,
          employer_id,
          status
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating form:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'employer_forms', action: 'create' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Invalidate cache
      await cacheService.setDashboardStats(employer_id, 'employer', null, 0);

      return { success: true, data };
    } catch (error) {
      console.error('Error in createForm:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'createForm' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Update an existing form
   * @param {string} formId - The form ID
   * @param {object} formData - Updated form data
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  updateForm: async (formId, formData, employerId) => {
    try {
      if (!formId || !employerId) {
        return { success: false, error: 'Form ID and employer ID are required' };
      }

      const { title, description, questions, status } = formData;

      // Build update object with only provided fields
      const updates = {};
      
      if (title !== undefined) {
        if (title.length < 1 || title.length > 200) {
          return { success: false, error: 'Title must be between 1 and 200 characters' };
        }
        updates.title = title;
      }

      if (description !== undefined) {
        if (description && description.length > 1000) {
          return { success: false, error: 'Description must not exceed 1000 characters' };
        }
        updates.description = description || null;
      }

      if (questions !== undefined) {
        updates.questions = Array.isArray(questions) ? questions : [];
      }

      if (status !== undefined) {
        if (!['draft', 'published'].includes(status)) {
          return { success: false, error: 'Status must be either draft or published' };
        }
        updates.status = status;
      }

      const { data, error } = await supabase
        .from('employer_forms')
        .update(updates)
        .eq('id', formId)
        .eq('employer_id', employerId)
        .select()
        .single();

      if (error) {
        console.error('Error updating form:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'employer_forms', action: 'update' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Invalidate cache
      await cacheService.setDashboardStats(employerId, 'employer', null, 0);

      return { success: true, data };
    } catch (error) {
      console.error('Error in updateForm:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'updateForm' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Delete a form
   * @param {string} formId - The form ID
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  deleteForm: async (formId, employerId) => {
    try {
      if (!formId || !employerId) {
        return { success: false, error: 'Form ID and employer ID are required' };
      }

      // Check if form is attached to any active jobs
      const { data: jobForms, error: jobFormsError } = await supabase
        .from('job_forms')
        .select('jobs(id, status)')
        .eq('form_id', formId);

      if (jobFormsError) {
        console.error('Error checking job forms:', jobFormsError);
        const supabaseError = new SupabaseError(jobFormsError.message, jobFormsError, { table: 'job_forms', action: 'check' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      const activeJobs = jobForms?.filter(jf => 
        jf.jobs && ['published', 'active'].includes(jf.jobs.status)
      );

      if (activeJobs && activeJobs.length > 0) {
        return { 
          success: false, 
          error: `Cannot delete form. It is attached to ${activeJobs.length} active job(s). Please unpublish or detach the form first.` 
        };
      }

      // Delete the form (cascade will handle job_forms and form_responses)
      const { error } = await supabase
        .from('employer_forms')
        .delete()
        .eq('id', formId)
        .eq('employer_id', employerId);

      if (error) {
        console.error('Error deleting form:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'employer_forms', action: 'delete' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Invalidate cache
      await cacheService.setDashboardStats(employerId, 'employer', null, 0);

      return { success: true };
    } catch (error) {
      console.error('Error in deleteForm:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'deleteForm' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Duplicate a form (creates a new draft copy)
   * @param {string} formId - The form ID to duplicate
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  duplicateForm: async (formId, employerId) => {
    try {
      if (!formId || !employerId) {
        return { success: false, error: 'Form ID and employer ID are required' };
      }

      // Fetch the original form
      const { data: originalForm, error: fetchError } = await supabase
        .from('employer_forms')
        .select('title, description, questions')
        .eq('id', formId)
        .eq('employer_id', employerId)
        .single();

      if (fetchError) {
        console.error('Error fetching form to duplicate:', fetchError);
        const supabaseError = new SupabaseError(fetchError.message, fetchError, { table: 'employer_forms', action: 'fetch_for_duplicate' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Create new form with "Copy" suffix
      const newTitle = `${originalForm.title} (Copy)`;
      
      const { data: newForm, error: createError } = await supabase
        .from('employer_forms')
        .insert({
          title: newTitle,
          description: originalForm.description,
          questions: originalForm.questions,
          employer_id: employerId,
          status: 'draft'
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating duplicate form:', createError);
        const supabaseError = new SupabaseError(createError.message, createError, { table: 'employer_forms', action: 'duplicate' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      // Invalidate cache
      await cacheService.setDashboardStats(employerId, 'employer', null, 0);

      return { success: true, data: newForm };
    } catch (error) {
      console.error('Error in duplicateForm:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'duplicateForm' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Publish a form (change status to published)
   * @param {string} formId - The form ID
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  publishForm: async (formId, employerId) => {
    try {
      // Validate that form has at least one question
      const formResult = await formService.getFormById(formId, employerId);
      if (!formResult.success) {
        return formResult;
      }

      if (!formResult.data.questions || formResult.data.questions.length === 0) {
        return { success: false, error: 'Cannot publish a form without questions' };
      }

      return await formService.updateForm(formId, { status: 'published' }, employerId);
    } catch (error) {
      console.error('Error in publishForm:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'publishForm' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Unpublish a form (change status to draft)
   * @param {string} formId - The form ID
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  unpublishForm: async (formId, employerId) => {
    return await formService.updateForm(formId, { status: 'draft' }, employerId);
  },

  /**
   * Attach a form to a job
   * @param {string} formId - The form ID
   * @param {string} jobId - The job ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  attachFormToJob: async (formId, jobId) => {
    try {
      if (!formId || !jobId) {
        return { success: false, error: 'Form ID and job ID are required' };
      }

      // Check if already attached — use maybeSingle() so a missing row returns null
      // instead of a 406 error (which .single() throws when 0 rows match)
      const { data: existing, error: checkError } = await supabase
        .from('job_forms')
        .select('id')
        .eq('job_id', jobId)
        .eq('form_id', formId)
        .maybeSingle();

      if (checkError) {
        console.error('[formService] Error checking job_forms attachment:', checkError);
        const supabaseError = new SupabaseError(checkError.message, checkError, { table: 'job_forms', action: 'check_existing' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      if (existing) {
        return { success: true, data: existing, message: 'Form already attached to job' };
      }

      const { data, error } = await supabase
        .from('job_forms')
        .insert({ job_id: jobId, form_id: formId })
        .select()
        .single();

      if (error) {
        console.error('Error attaching form to job:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'job_forms', action: 'attach' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error in attachFormToJob:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'attachFormToJob' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Detach a form from a job
   * @param {string} formId - The form ID
   * @param {string} jobId - The job ID
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  detachFormFromJob: async (formId, jobId) => {
    try {
      if (!formId || !jobId) {
        return { success: false, error: 'Form ID and job ID are required' };
      }

      const { error } = await supabase
        .from('job_forms')
        .delete()
        .eq('job_id', jobId)
        .eq('form_id', formId);

      if (error) {
        console.error('Error detaching form from job:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'job_forms', action: 'detach' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      return { success: true };
    } catch (error) {
      console.error('Error in detachFormFromJob:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'detachFormFromJob' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Get all forms attached to a job
   * @param {string} jobId - The job ID
   * @returns {Promise<{success: boolean, data?: array, error?: string}>}
   */
  getJobForms: async (jobId) => {
    try {
      if (!jobId) {
        return { success: false, error: 'Job ID is required' };
      }

      const { data, error } = await supabase
        .from('job_forms')
        .select(`
          id,
          form_id,
          employer_forms!inner(
            id,
            employer_id,
            title,
            description,
            questions,
            status,
            created_at,
            updated_at
          )
        `)
        .eq('job_id', jobId)
        .eq('employer_forms.status', 'published');

      if (error) {
        console.error('Error fetching job forms:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'job_forms', action: 'fetch' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      const forms = (data || []).map(jf => jf.employer_forms).filter(Boolean);
      return { success: true, data: forms };    } catch (error) {
      console.error('Error in getJobForms:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'getJobForms' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Submit form responses (VA filling out form during application)
   * @param {string} formId - The form ID
   * @param {string} applicationId - The job application ID
   * @param {string} vaId - The VA's user ID
   * @param {object} responses - The form responses object
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  submitFormResponse: async (formId, applicationId, vaId, responses) => {
    try {
      if (!formId || !applicationId || !vaId) {
        return { success: false, error: 'Form ID, application ID, and VA ID are required' };
      }

      if (!responses || typeof responses !== 'object') {
        return { success: false, error: 'Responses must be a valid object' };
      }

      // Check if response already exists (upsert)
      const { data: existingResponse } = await supabase
        .from('form_responses')
        .select('id')
        .eq('form_id', formId)
        .eq('application_id', applicationId)
        .single();

      let result;
      if (existingResponse) {
        // Update existing response
        result = await supabase
          .from('form_responses')
          .update({ responses })
          .eq('id', existingResponse.id)
          .select()
          .single();
      } else {
        // Insert new response
        result = await supabase
          .from('form_responses')
          .insert({
            form_id: formId,
            application_id: applicationId,
            va_id: vaId,
            responses
          })
          .select()
          .single();
      }

      if (result.error) {
        console.error('Error submitting form response:', result.error);
        const supabaseError = new SupabaseError(result.error.message, result.error, { table: 'form_responses', action: 'submit' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      return { success: true, data: result.data };
    } catch (error) {
      console.error('Error in submitFormResponse:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'submitFormResponse' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Get all responses for a form (employer viewing responses)
   * @param {string} formId - The form ID
   * @param {string} employerId - The employer's user ID (for authorization)
   * @returns {Promise<{success: boolean, data?: array, error?: string}>}
   */
  getFormResponses: async (formId, employerId) => {
    try {
      if (!formId || !employerId) {
        return { success: false, error: 'Form ID and employer ID are required' };
      }

      const { data, error } = await supabase
        .from('form_responses')
        .select(`
          *,
          job_applications(
            id,
            status,
            job_id,
            applicant_id,
            user_profiles(full_name, profile_picture)
          )
        `)
        .eq('form_id', formId);

      if (error) {
        console.error('Error fetching form responses:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'form_responses', action: 'fetch' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getFormResponses:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'getFormResponses' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  },

  /**
   * Get form responses for a specific application
   * @param {string} applicationId - The job application ID
   * @returns {Promise<{success: boolean, data?: array, error?: string}>}
   */
  getApplicationFormResponses: async (applicationId) => {
    try {
      if (!applicationId) {
        return { success: false, error: 'Application ID is required' };
      }

      const { data, error } = await supabase
        .from('form_responses')
        .select(`
          *,
          employer_forms(title, description, questions)
        `)
        .eq('application_id', applicationId);

      if (error) {
        console.error('Error fetching application form responses:', error);
        const supabaseError = new SupabaseError(error.message, error, { table: 'form_responses', action: 'fetch_by_application' });
        return { success: false, error: supabaseError.getUserFriendlyMessage() };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getApplicationFormResponses:', error);
      const supabaseError = new SupabaseError(error.message, error, { function: 'getApplicationFormResponses' });
      return { success: false, error: supabaseError.getUserFriendlyMessage() };
    }
  }
};

export default formService;
